/**
 * Admin Model Management
 *
 * Superadmin panel to discover, enable/disable, and review LLM models.
 * Models are fetched from the AWS backend's model registry.
 */
import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import { getAuthToken } from '../../utils/auth';

const CLOUD_API_BASE = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';

const authHeaders = () => ({
    'Authorization': `Bearer ${getAuthToken()}`,
    'Content-Type': 'application/json',
});

const api = {
    listModels: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.provider) params.set('provider', filters.provider);
        if (filters.status) params.set('status', filters.status);
        if (filters.enabled !== undefined && filters.enabled !== '') params.set('enabled', filters.enabled);
        const resp = await fetch(`${CLOUD_API_BASE}/api/admin/models?${params}`, { headers: authHeaders() });
        if (!resp.ok) throw new Error('Failed to fetch models');
        return resp.json();
    },
    toggleModel: async (id, enabled) => {
        const resp = await fetch(`${CLOUD_API_BASE}/api/admin/models/${id}`, {
            method: 'PUT', headers: authHeaders(), body: JSON.stringify({ enabled }),
        });
        if (!resp.ok) throw new Error('Failed to toggle model');
        return resp.json();
    },
    bulkToggle: async (modelIds, enabled) => {
        const resp = await fetch(`${CLOUD_API_BASE}/api/admin/models/bulk-toggle`, {
            method: 'PUT', headers: authHeaders(), body: JSON.stringify({ model_ids: modelIds, enabled }),
        });
        if (!resp.ok) throw new Error('Failed to bulk toggle');
        return resp.json();
    },
    discover: async (providers) => {
        const params = providers ? providers.map(p => `providers=${p}`).join('&') : '';
        const resp = await fetch(`${CLOUD_API_BASE}/api/admin/models/discover${params ? '?' + params : ''}`, {
            method: 'POST', headers: authHeaders(),
        });
        if (!resp.ok) throw new Error('Discovery failed');
        return resp.json();
    },
    getProviders: async () => {
        const resp = await fetch(`${CLOUD_API_BASE}/api/admin/models/providers`, { headers: authHeaders() });
        if (!resp.ok) throw new Error('Failed to fetch providers');
        return resp.json();
    },
    deleteModel: async (id) => {
        const resp = await fetch(`${CLOUD_API_BASE}/api/admin/models/${id}`, {
            method: 'DELETE', headers: authHeaders(),
        });
        if (!resp.ok) throw new Error('Failed to delete model');
        return resp.json();
    },
};

const STATUS_COLORS = {
    active: { bg: 'rgba(34,197,94,0.1)', color: '#22c55e', border: 'rgba(34,197,94,0.25)' },
    new: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6', border: 'rgba(59,130,246,0.25)' },
    deprecated: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'rgba(239,68,68,0.25)' },
};

const PROVIDER_COLORS = {
    bedrock: '#ff9900',
    anthropic: '#d97706',
    openai: '#10a37f',
    gemini: '#4285f4',
};

const RefreshIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
);

const ToggleSwitch = ({ checked, onChange, disabled }) => (
    <button
        className={`model-toggle ${checked ? 'active' : ''}`}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        aria-label={checked ? 'Disable' : 'Enable'}
    />
);

export function AdminModels() {
    const [models, setModels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [message, setMessage] = useState(null);
    const [configuredProviders, setConfiguredProviders] = useState({});
    const [selected, setSelected] = useState(new Set());

    // Filters
    const [filterProvider, setFilterProvider] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterEnabled, setFilterEnabled] = useState('');
    const [searchText, setSearchText] = useState('');

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 4000);
    };

    const loadModels = useCallback(async () => {
        setIsLoading(true);
        try {
            const [data, providers] = await Promise.all([
                api.listModels({ provider: filterProvider, status: filterStatus, enabled: filterEnabled }),
                api.getProviders(),
            ]);
            setModels(data);
            setConfiguredProviders(providers);
        } catch (err) {
            showMessage('error', err.message);
        } finally {
            setIsLoading(false);
        }
    }, [filterProvider, filterStatus, filterEnabled]);

    useEffect(() => { loadModels(); }, [loadModels]);

    const handleDiscover = async () => {
        setIsDiscovering(true);
        try {
            const summary = await api.discover();
            showMessage('success',
                `Discovery complete: ${summary.new} new, ${summary.updated} refreshed, ${summary.deprecated} deprecated (${summary.total} total)`
            );
            loadModels();
        } catch (err) {
            showMessage('error', `Discovery failed: ${err.message}`);
        } finally {
            setIsDiscovering(false);
        }
    };

    const handleToggle = async (model) => {
        try {
            await api.toggleModel(model.id, !model.enabled);
            setModels(prev => prev.map(m => m.id === model.id ? { ...m, enabled: !m.enabled } : m));
        } catch (err) {
            showMessage('error', err.message);
        }
    };

    const handleBulkToggle = async (enabled) => {
        if (selected.size === 0) return;
        try {
            await api.bulkToggle([...selected], enabled);
            showMessage('success', `${enabled ? 'Enabled' : 'Disabled'} ${selected.size} models`);
            setSelected(new Set());
            loadModels();
        } catch (err) {
            showMessage('error', err.message);
        }
    };

    const handleDelete = async (model) => {
        if (!window.confirm(`Remove "${model.display_name || model.model_id}" from the registry?`)) return;
        try {
            await api.deleteModel(model.id);
            setModels(prev => prev.filter(m => m.id !== model.id));
            showMessage('success', 'Model removed');
        } catch (err) {
            showMessage('error', err.message);
        }
    };

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selected.size === filteredModels.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filteredModels.map(m => m.id)));
        }
    };

    // Client-side text search on top of server filters
    const filteredModels = models.filter(m => {
        if (!searchText) return true;
        const q = searchText.toLowerCase();
        return (m.model_id?.toLowerCase().includes(q)) ||
               (m.display_name?.toLowerCase().includes(q)) ||
               (m.provider?.toLowerCase().includes(q));
    });

    // Group by provider for display
    const grouped = {};
    for (const m of filteredModels) {
        if (!grouped[m.provider]) grouped[m.provider] = [];
        grouped[m.provider].push(m);
    }

    const providerKeys = Object.keys(configuredProviders);
    const stats = {
        total: models.length,
        enabled: models.filter(m => m.enabled).length,
        new: models.filter(m => m.status === 'new').length,
        deprecated: models.filter(m => m.status === 'deprecated').length,
    };

    return (
        <div className="admin-users">
            <div className="admin-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h1>Model Management</h1>
                        <p>Discover and manage LLM models available in the Agent Designer.</p>
                    </div>
                    <button
                        className="action-btn"
                        onClick={handleDiscover}
                        disabled={isDiscovering}
                        style={{
                            background: 'rgba(59,130,246,0.15)',
                            color: '#3b82f6',
                            border: '1px solid rgba(59,130,246,0.3)',
                            padding: '8px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                        }}
                    >
                        <span className={isDiscovering ? 'spin' : ''} style={{ display: 'flex' }}><RefreshIcon /></span>
                        {isDiscovering ? 'Discovering...' : 'Run Discovery'}
                    </button>
                </div>
            </div>

            {/* Stats bar */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <StatCard label="Total Models" value={stats.total} />
                <StatCard label="Enabled" value={stats.enabled} color="#22c55e" />
                <StatCard label="New" value={stats.new} color="#3b82f6" />
                <StatCard label="Deprecated" value={stats.deprecated} color="#ef4444" />
                <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                    justifyContent: 'flex-end', flexWrap: 'wrap', minWidth: '200px'
                }}>
                    {providerKeys.map(p => (
                        <span key={p} style={{
                            fontSize: '11px',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: configuredProviders[p]
                                ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                            color: configuredProviders[p] ? '#22c55e' : '#ef4444',
                            border: `1px solid ${configuredProviders[p]
                                ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                        }}>
                            {p} {configuredProviders[p] ? '✓' : '✗'}
                        </span>
                    ))}
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Search models..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    className="tier-input"
                    style={{ width: '200px', padding: '6px 10px', fontSize: '13px' }}
                />
                <select value={filterProvider} onChange={e => setFilterProvider(e.target.value)} className="tier-input" style={{ padding: '6px 10px', fontSize: '13px' }}>
                    <option value="">All Providers</option>
                    {providerKeys.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="tier-input" style={{ padding: '6px 10px', fontSize: '13px' }}>
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="new">New</option>
                    <option value="deprecated">Deprecated</option>
                </select>
                <select value={filterEnabled} onChange={e => setFilterEnabled(e.target.value)} className="tier-input" style={{ padding: '6px 10px', fontSize: '13px' }}>
                    <option value="">Enabled & Disabled</option>
                    <option value="true">Enabled only</option>
                    <option value="false">Disabled only</option>
                </select>

                {selected.size > 0 && (
                    <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                        <span style={{ fontSize: '12px', color: 'var(--theme-text-secondary)', alignSelf: 'center' }}>
                            {selected.size} selected
                        </span>
                        <button className="action-btn" onClick={() => handleBulkToggle(true)}
                            style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: '12px', padding: '4px 10px' }}>
                            Enable
                        </button>
                        <button className="action-btn" onClick={() => handleBulkToggle(false)}
                            style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', padding: '4px 10px' }}>
                            Disable
                        </button>
                    </div>
                )}
            </div>

            {message && (
                <div style={{
                    padding: '10px 16px', marginBottom: '16px', borderRadius: '8px',
                    background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    color: message.type === 'success' ? '#22c55e' : '#ef4444',
                    border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                    fontSize: '13px',
                }}>
                    {message.text}
                </div>
            )}

            {/* Table */}
            <div className="admin-table-container">
                {isLoading ? (
                    <div className="table-loading"><div className="spinner"></div></div>
                ) : filteredModels.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--theme-text-secondary)' }}>
                        <p style={{ fontSize: '15px', marginBottom: '8px' }}>No models found</p>
                        <p style={{ fontSize: '13px' }}>Click "Run Discovery" to scan provider APIs for available models.</p>
                    </div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th style={{ width: '36px' }}>
                                    <input type="checkbox"
                                        checked={selected.size === filteredModels.length && filteredModels.length > 0}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th>Provider</th>
                                <th>Model ID</th>
                                <th>Display Name</th>
                                <th>Status</th>
                                <th>API Key</th>
                                <th style={{ width: '80px' }}>Enabled</th>
                                <th style={{ width: '60px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredModels.map(m => {
                                const sc = STATUS_COLORS[m.status] || STATUS_COLORS.active;
                                const pc = PROVIDER_COLORS[m.provider] || '#888';
                                return (
                                    <tr key={m.id} style={{ opacity: m.status === 'deprecated' ? 0.6 : 1 }}>
                                        <td>
                                            <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} />
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                                                background: `${pc}15`, color: pc, border: `1px solid ${pc}30`,
                                                fontWeight: 500, textTransform: 'capitalize',
                                            }}>
                                                {m.provider}
                                            </span>
                                        </td>
                                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{m.model_id}</td>
                                        <td style={{ fontSize: '13px', color: 'var(--theme-text-secondary)' }}>
                                            {m.display_name || '—'}
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                                                background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                                                fontWeight: 500, textTransform: 'capitalize',
                                            }}>
                                                {m.status}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '12px', color: 'var(--theme-text-tertiary)' }}>
                                            {m.requires_api_key ? 'Required' : '—'}
                                        </td>
                                        <td>
                                            <ToggleSwitch checked={m.enabled} onChange={() => handleToggle(m)} />
                                        </td>
                                        <td>
                                            {m.status === 'deprecated' && (
                                                <button className="action-btn"
                                                    onClick={() => handleDelete(m)}
                                                    title="Remove"
                                                    style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '11px', padding: '3px 8px' }}>
                                                    ✕
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <div style={{ marginTop: '16px', color: 'var(--theme-text-tertiary)', fontSize: '12px' }}>
                <p><strong>How it works:</strong> "Run Discovery" queries each provider API (Bedrock, Anthropic, OpenAI, Gemini)
                    for its current model catalog. New models appear with a <em>new</em> badge. Models no longer returned by APIs
                    are marked <em>deprecated</em>. Only <em>enabled</em> models appear in the Agent Designer dropdown.</p>
            </div>
        </div>
    );
}

function StatCard({ label, value, color }) {
    return (
        <div style={{
            padding: '10px 16px',
            borderRadius: '8px',
            background: 'var(--theme-bg-secondary, #1a1a2e)',
            border: '1px solid var(--theme-border, #333)',
            minWidth: '100px',
        }}>
            <div style={{ fontSize: '11px', color: 'var(--theme-text-tertiary)', marginBottom: '2px' }}>{label}</div>
            <div style={{ fontSize: '20px', fontWeight: 600, color: color || 'var(--theme-text-primary)' }}>{value}</div>
        </div>
    );
}

export default AdminModels;
